/**
 * lib/admin.ts — the administration console's section table, stated ONCE.
 *
 * Four consumers read this and therefore cannot disagree with one another: the primary-navigation
 * entry, the account menu's entry, the route dispatch in `App.tsx`, and the console's own section
 * strip. Before the console existed the four administrative screens were flat peers of the work screen
 * and each carried its own gate expression inline; a fifth would have been a fifth copy.
 *
 * **The gate is `canReachAdmin`, and it is the whole Decision rather than half of it.** Three things it
 * deliberately is not:
 *
 *   - **not `me.user.isSuperuser`.** A delegate holding `audit.view` is entitled by `DELEGATE_ROUTES`
 *     to reach four of the seven delegable reads. Gating the console on the superuser flag would hide
 *     from them a console whose contents they hold.
 *   - **not `me.sections.users.view`.** The same failure, narrower: it would admit a People delegate and
 *     refuse an Audit one, for no reason either could discover.
 *   - **does not consult `sections.jobs`.** It exists in the enum and is consulted by no route on the
 *     server, so an entry drawn from it would be a guess. `App.tsx` says the same thing about the
 *     authoring controls, for the same reason.
 *
 * Hiding and refusing must agree, so the route dispatch uses this same expression: a deep link to
 * `#/admin` from an ungated subject reaches the console's denied state rather than a blank page. None
 * of this is enforcement — `requireAdminRoute` re-checks every call on the server. It is UX, and UX
 * that lies about what a person holds is the thing this file exists to stop.
 */
import type { AdminSection, Me } from "./api";

/** The console's sections, addressed as URL segments. Distinct from the wire's `AdminSection` enum. */
export type AdminConsoleSection = "people" | "access" | "business-units" | "audit";

export interface AdminSectionSpec {
  readonly key: AdminConsoleSection;
  readonly label: string;
  /** The full hash address, so no consumer builds one by string concatenation. */
  readonly href: string;
  /** The `/api/me` section key this destination is gated on. */
  readonly section: AdminSection;
  /**
   * One sentence answering the question an administrator arrives with, **for a caller who may change
   * things** — which on this surface means the superuser and nobody else.
   *
   * This is the landing page's whole reason to exist: a strip can only NAME a section, and "where do I
   * revoke someone's access?" is not answered by the word *Access*.
   */
  readonly blurb: string;
  /**
   * The same question answered for a caller who may only READ.
   *
   * ⚠️ **Not a nicety — the single blurb was a lie in prose.** Every entry in `DELEGATE_ROUTES` is a
   * read, so a delegate holding `users.view` can change nothing at all; they were nonetheless told to
   * *"admit a new joiner, suspend somebody who has left, … and choose which business unit a person
   * belongs to"*, and following the card got them the words *View only* where the buttons would be.
   * `CLAUDE.md` forbids exactly this as a button — "a control that always 403s, which is a worse lie
   * than a missing one" — and moving the same promise into a sentence does not make it true.
   */
  readonly readBlurb: string;
  /** True where the screen reads one business unit's data. Audit alone — see `App.tsx`'s console branch. */
  readonly scoped: boolean;
}

export const ADMIN_CONSOLE_SECTIONS: readonly AdminSectionSpec[] = [
  {
    key: "people",
    label: "People",
    href: "#/admin/people",
    section: "users",
    scoped: false,
    blurb:
      "Admit a new joiner, suspend somebody who has left, see who has never signed in, and choose which business unit a person belongs to.",
    readBlurb:
      "See who has an account, which business unit each of them belongs to, who has never signed in, and when each was last here. Admitting, suspending and assigning are the superuser's.",
  },
  {
    key: "access",
    label: "Access",
    href: "#/admin/access",
    section: "access",
    scoped: false,
    blurb:
      "Groups are the only carrier of rights. Create one, change who is in it, and grant or revoke what it reaches — this is where access is taken away.",
    readBlurb:
      "Groups are the only carrier of rights. Read which groups exist, who is in each, and exactly what each one reaches — the answer to “why can this person see that?”. Changing them is the superuser's.",
  },
  {
    key: "business-units",
    label: "Business units",
    href: "#/admin/business-units",
    section: "business_units",
    scoped: false,
    blurb:
      "Create a unit, point it at a workspace and a git host, enable or disable it — and see how many people a change would strand before making it.",
    readBlurb:
      "See which units exist, where each one points — workspace, git host, config repository — and whether it is switched on. Creating one and enabling it are the superuser's.",
  },
  {
    key: "audit",
    label: "Audit",
    href: "#/admin/audit",
    section: "audit",
    scoped: true,
    // Audit is a reading in both directions: it carries no verb for anybody, superuser included, so the
    // two sentences are deliberately the same one rather than artificially different.
    blurb:
      "Every administrative act in one business unit: who did it, to whom, when, and whether it was allowed or refused.",
    readBlurb:
      "Every administrative act in one business unit: who did it, to whom, when, and whether it was allowed or refused.",
  },
];

/**
 * The sentence this subject should actually read on the landing page.
 *
 * One boolean, and it is the right one: every mutation on this whole administrative surface is
 * superuser-only by omission from `DELEGATE_ROUTES`, which is why `App.tsx` draws every action button on
 * every one of these screens from `me.user.isSuperuser` too. A section-level `act` would be the wrong
 * predicate — it is inert across the entire surface, so it would promise the verbs to a delegate again.
 */
export function blurbFor(spec: AdminSectionSpec, canChange: boolean): string {
  return canChange ? spec.blurb : spec.readBlurb;
}

/** The sections this subject may see. Order follows the table, so the strip and the cards agree. */
export function visibleAdminSections(me: Me): readonly AdminSectionSpec[] {
  return ADMIN_CONSOLE_SECTIONS.filter((spec) => me.sections[spec.section].view);
}

/**
 * Whether the console is reachable at all — the union of the four sections.
 *
 * Note what is NOT folded in: whether a business unit is reachable. Audit is scoped and the other three
 * are not, so a subject holding only `audit.view` on a fresh deployment still reaches the console and is
 * asked for a unit *inside* it. Folding scope into this gate would take the console away from the one
 * person who could tell you why there is no unit yet.
 */
export function canReachAdmin(me: Me): boolean {
  return visibleAdminSections(me).length > 0;
}
